import React from "react";
import { playFavouriteSound, playCloseSound } from "../../Sounds";
import { getCardStats } from "../social/TopTrumps";
import imageData from "../../data/imageData";

const RARITY_COLOURS = {
  Common:    "#9e9e9e",
  Uncommon:  "#4caf50",
  Rare:      "#2196f3",
  Epic:      "#9c27b0",
  Legendary: "#ff9800",
  Mythic:    "#ffeb3b",
  Ultra:     "#ff2d78",
};

const COIN_VALUES = {
  Common: 3, Uncommon: 8, Rare: 20, Epic: 50,
  Legendary: 120, Mythic: 300, Ultra: 1000,
};

const STAT_META = [
  { key: "power",   label: "⚔️ Power",   color: "#ff4444" },
  { key: "speed",   label: "⚡ Speed",   color: "#00e5ff" },
  { key: "cunning", label: "🧠 Cunning", color: "#bf5fff" },
  { key: "aura",    label: "🌟 Aura",    color: "#ffd700" },
];

const SHATTER_THRESHOLD = 3;
function getShatterPayout(rarity) {
  return { Common: 25, Uncommon: 60, Rare: 150, Epic: 400, Legendary: 900, Mythic: 2200, Ultra: 7500 }[rarity] || 25;
}

export default function HamsterDetail({ hamster, pullStats, favourites = [], onToggleFavourite, onShatter, onClose }) {
  if (!hamster) return null;

  const live       = imageData.find(d => d.name === hamster.name) || {};
  const flavour    = live.flavour_text || null;
  const colour     = RARITY_COLOURS[hamster.rarity] || "#fff";
  const coinValue  = COIN_VALUES[hamster.rarity] || 0;
  const stats      = pullStats?.[hamster.name] || { count: 1, firstPull: null };
  const dupeCount  = Math.max(0, stats.count - 1);
  const isFav      = favourites.includes(hamster.name);
  const cardStats  = getCardStats(hamster);
  const canShatter = dupeCount >= SHATTER_THRESHOLD;
  const shatterPay = getShatterPayout(hamster.rarity);

  const firstPullDate = stats.firstPull
    ? new Date(stats.firstPull).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "Unknown";

  return (
    <div className="modal-overlay" onClick={() => { playCloseSound(); onClose(); }}>
      <div className="modal-box detail-box" onClick={e => e.stopPropagation()}>

        {/* Favourite button */}
        {onToggleFavourite && (
          <button
            className={`detail-fav-btn${isFav ? " active" : ""}`}
            onClick={e => { playFavouriteSound(); onToggleFavourite(hamster.name, e); }}
            title={isFav ? "Remove from favourites" : "Add to favourites"}
          >
            {isFav ? "⭐" : "☆"}
          </button>
        )}

        {/* Image */}
        <div className="detail-img-wrap">
          <div className="detail-rarity-glow" style={{ background: colour }} />
          <img src={hamster.image} alt={hamster.name} className="detail-img" style={{ borderColor: colour }} />
        </div>

        {/* Name & rarity */}
        <div className="detail-name">{hamster.name}</div>
        <div className="detail-rarity-badge" style={{ color: colour, background: `${colour}18` }}>
          ✦ {hamster.rarity}
        </div>

        {/* Flavour text */}
        {flavour && <div className="detail-flavour">"{flavour}"</div>}

        {/* Pull stats */}
        <div className="detail-stats">
          <div className="detail-stat">
            <div className="detail-stat-value" style={{ color: colour }}>{stats.count}</div>
            <div className="detail-stat-label">Times Pulled</div>
          </div>
          <div className="detail-stat">
            <div className="detail-stat-value">{dupeCount}</div>
            <div className="detail-stat-label">Duplicates</div>
          </div>
          <div className="detail-stat">
            <div className="detail-stat-value">🪙 {coinValue * 2}</div>
            <div className="detail-stat-label">New Pull</div>
          </div>
          <div className="detail-stat">
            <div className="detail-stat-value">🪙 {coinValue}</div>
            <div className="detail-stat-label">Dupe Coins</div>
          </div>
        </div>

        {/* Battle stats */}
        <div className="detail-battle-stats">
          <div className="detail-battle-title">⚔️ Battle Stats</div>
          {STAT_META.map(s => {
            const val = cardStats[s.key];
            return (
              <div key={s.key} className="detail-battle-row">
                <span className="detail-battle-label">{s.label}</span>
                <div className="detail-battle-bar-wrap">
                  <div className="detail-battle-bar-fill" style={{ width: `${val}%`, background: s.color, boxShadow: `0 0 8px ${s.color}88` }} />
                </div>
                <span className="detail-battle-val" style={{ color: s.color }}>{val}</span>
              </div>
            );
          })}
        </div>

        {/* Shatter */}
        {dupeCount > 0 && (
          <div className={`detail-shatter-wrap${canShatter ? " ready" : ""}`}>
            <div className="detail-shatter-info">
              <span className="detail-shatter-icon">💎</span>
              <div>
                <div className="detail-shatter-title">Shatter Duplicates</div>
                <div className="detail-shatter-sub">
                  {canShatter
                    ? `Destroy ${dupeCount} dupe${dupeCount > 1 ? "s" : ""} for 🪙 ${shatterPay} coins`
                    : `${SHATTER_THRESHOLD - dupeCount} more dupe${SHATTER_THRESHOLD - dupeCount > 1 ? "s" : ""} needed to shatter`}
                </div>
              </div>
            </div>
            <div className="detail-shatter-dots">
              {Array.from({ length: SHATTER_THRESHOLD }).map((_, i) => (
                <div key={i} className={`detail-shatter-dot${i < dupeCount ? " filled" : ""}`} />
              ))}
            </div>
            {canShatter && onShatter && (
              <button className="detail-shatter-btn" onClick={() => onShatter(hamster, dupeCount, shatterPay)}>
                💥 Shatter for 🪙 {shatterPay}
              </button>
            )}
          </div>
        )}

        {/* First pull */}
        <div className="detail-first-pull">
          First pulled: <span>{firstPullDate}</span>
        </div>

        <button className="back-button" onClick={() => { playCloseSound(); onClose(); }} style={{ width: "100%", marginTop: 12 }}>
          Close
        </button>

      </div>
    </div>
  );
}